import { useState } from "react";
import { createFileRoute, redirect, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { getMyAdminStatus } from "@/lib/billing.functions";
import {
  listAdminFailures,
  getAdminFailureDetail,
  type FailureRow,
  type FailureSource,
} from "@/lib/admin-failures.functions";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

export const Route = createFileRoute("/_authed/admin/failures")({
  head: () => ({
    meta: [
      { title: "Admin: Pipeline failures: CapitalDart" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  beforeLoad: async () => {
    if (typeof window === "undefined") return;
    const status = await getMyAdminStatus();
    if (!status.isAdmin) throw redirect({ to: "/dashboard" });
  },
  component: AdminFailuresPage,
});

const PAGE_SIZE = 20;

type SourceFilter = "all" | FailureSource;

const SOURCE_LABEL: Record<FailureSource, string> = {
  run: "Pipeline run",
  irr: "IRR / Deck Studio",
  event: "Event",
};

function AdminFailuresPage() {
  const [days, setDays] = useState(30);
  const [source, setSource] = useState<SourceFilter>("all");
  const [page, setPage] = useState(1);
  const [includeInternal, setIncludeInternal] = useState(false);
  const [openKey, setOpenKey] = useState<
    { source: FailureSource; id: string } | null
  >(null);

  const fetchList = useServerFn(listAdminFailures);
  const q = useQuery({
    queryKey: ["admin-failures", days, source, page, includeInternal],
    queryFn: () =>
      fetchList({
        data: {
          days,
          page,
          page_size: PAGE_SIZE,
          include_internal: includeInternal,
          ...(source !== "all" ? { source } : {}),
        },
      }),
    refetchOnWindowFocus: false,
  });

  const totalPages = q.data
    ? Math.max(1, Math.ceil(q.data.total / PAGE_SIZE))
    : 1;

  return (
    <main className="mx-auto max-w-[1180px] px-4 pb-16 pt-6">
      <header className="mb-6">
        <Link
          to="/admin"
          className="text-[12.5px] text-brand-muted no-underline hover:text-brand-ink"
        >
          ← Back to admin
        </Link>
        <h1 className="mt-2 font-serif text-[32px] font-normal leading-tight tracking-tight text-brand-ink">
          Pipeline failures.
        </h1>
        <p className="mt-1 text-[14px] text-brand-muted">
          Union of failed runs, failed IRR/Deck Studio reports, and explicit
          failure events. Click a row to see the underlying error and raw
          context.
        </p>
      </header>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <span className="text-[13px] text-brand-muted">Recency</span>
        <Select
          value={String(days)}
          onValueChange={(v) => {
            setDays(Number(v));
            setPage(1);
          }}
        >
          <SelectTrigger className="w-[160px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="1">Last 24h</SelectItem>
            <SelectItem value="7">Last 7 days</SelectItem>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
          </SelectContent>
        </Select>

        <span className="ml-2 text-[13px] text-brand-muted">Source</span>
        <Select
          value={source}
          onValueChange={(v) => {
            setSource(v as SourceFilter);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sources</SelectItem>
            <SelectItem value="run">Pipeline runs</SelectItem>
            <SelectItem value="irr">IRR / Deck Studio</SelectItem>
            <SelectItem value="event">Failure events</SelectItem>
          </SelectContent>
        </Select>

        <label className="ml-2 flex items-center gap-2 text-[12.5px] text-brand-muted">
          <input
            type="checkbox"
            checked={includeInternal}
            onChange={(e) => {
              setIncludeInternal(e.target.checked);
              setPage(1);
            }}
          />
          Include internal activity
        </label>

        {q.data && (
          <span className="ml-auto text-[12.5px] text-brand-muted">
            {q.data.total.toLocaleString()} total
          </span>
        )}
      </div>

      <section className="rounded-2xl border border-brand-line bg-brand-paper p-2">
        {q.isLoading && (
          <div className="grid gap-2 p-3">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-9 w-full" />
            ))}
          </div>
        )}
        {q.error && (
          <p className="p-4 text-[14px] text-destructive">
            {q.error instanceof Error ? q.error.message : "Failed to load"}
          </p>
        )}
        {q.data && q.data.rows.length === 0 && (
          <p className="m-3 rounded-lg border border-dashed border-brand-line bg-background/50 px-4 py-6 text-center text-[14px] text-brand-muted">
            No failures in this window.
          </p>
        )}
        {q.data && q.data.rows.length > 0 && (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[160px]">When (UTC)</TableHead>
                  <TableHead className="w-[120px]">Source</TableHead>
                  <TableHead className="w-[180px]">User</TableHead>
                  <TableHead className="w-[180px]">Deal</TableHead>
                  <TableHead className="w-[110px]">Stage</TableHead>
                  <TableHead>Error</TableHead>
                  <TableHead className="w-[130px]">Refund</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {q.data.rows.map((r) => (
                  <FailureRowItem
                    key={`${r.source}:${r.source_id}`}
                    row={r}
                    onOpen={() =>
                      setOpenKey({ source: r.source, id: r.source_id })
                    }
                  />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </section>

      <div className="mt-4 flex items-center justify-between">
        <span className="text-[12.5px] text-brand-muted">
          Page {page} of {totalPages}
        </span>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={page <= 1 || q.isFetching}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
          >
            Previous
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={page >= totalPages || q.isFetching}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      </div>

      <FailureDrawer
        target={openKey}
        onOpenChange={(o) => !o && setOpenKey(null)}
      />
    </main>
  );
}

function FailureRowItem({
  row,
  onOpen,
}: {
  row: FailureRow;
  onOpen: () => void;
}) {
  return (
    <TableRow className="cursor-pointer hover:bg-muted/40" onClick={onOpen}>
      <TableCell className="font-mono text-[12px] text-brand-muted">
        {new Date(row.occurred_at).toISOString().replace("T", " ").slice(0, 19)}
      </TableCell>
      <TableCell className="text-[12.5px]">{SOURCE_LABEL[row.source]}</TableCell>
      <TableCell className="text-[12.5px] text-brand-ink">
        {row.user_email ?? (
          <span className="text-brand-muted">
            {row.user_id ? row.user_id.slice(0, 8) + "…" : "—"}
          </span>
        )}
      </TableCell>
      <TableCell className="text-[12.5px]">
        {row.deal_name ?? (
          <span className="font-mono text-brand-muted">
            {row.deal_id ? row.deal_id.slice(0, 8) + "…" : "—"}
          </span>
        )}
      </TableCell>
      <TableCell className="text-[12.5px] text-brand-muted">
        {row.stage ?? "—"}
      </TableCell>
      <TableCell className="text-[12.5px] text-brand-muted">
        <div className="line-clamp-1 font-medium text-brand-ink">
          {row.error_class ?? "unknown"}
          {row.persisted_error_class ? (
            <span className="ml-1.5 rounded border border-brand-line px-1 py-px font-mono text-[10.5px] font-normal text-brand-muted">
              {row.persisted_error_class}
            </span>
          ) : null}
        </div>
        <div className="line-clamp-1">{row.error_message ?? "—"}</div>
      </TableCell>
      <TableCell className="text-[12px] text-brand-muted">
        {row.refund_disposition ?? "—"}
      </TableCell>
    </TableRow>
  );
}

function FailureDrawer({
  target,
  onOpenChange,
}: {
  target: { source: FailureSource; id: string } | null;
  onOpenChange: (open: boolean) => void;
}) {
  const fetchDetail = useServerFn(getAdminFailureDetail);
  const q = useQuery({
    queryKey: ["admin-failure-detail", target?.source, target?.id],
    queryFn: () =>
      fetchDetail({
        data: { source: target!.source, id: target!.id },
      }),
    enabled: !!target,
    refetchOnWindowFocus: false,
  });

  return (
    <Sheet open={!!target} onOpenChange={onOpenChange}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-[640px]">
        <SheetHeader>
          <SheetTitle className="font-serif text-[22px] text-brand-ink">
            Failure details
          </SheetTitle>
          <SheetDescription>
            Full error message and the raw context payload.
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 grid gap-4 px-4 pb-6">
          {q.isLoading && (
            <div className="grid gap-2">
              <Skeleton className="h-6 w-1/3" />
              <Skeleton className="h-6 w-1/2" />
              <Skeleton className="h-32 w-full" />
            </div>
          )}
          {q.error && (
            <p className="text-[14px] text-destructive">
              {q.error instanceof Error ? q.error.message : "Failed to load"}
            </p>
          )}
          {q.data && (
            <>
              <DetailRow label="Occurred at">
                <span className="font-mono text-[12.5px] text-brand-muted">
                  {new Date(q.data.occurred_at).toISOString()}
                </span>
              </DetailRow>
              <DetailRow label="Source">
                {SOURCE_LABEL[q.data.source]}
              </DetailRow>
              <DetailRow label="Source id">
                <span className="font-mono text-[12px]">{q.data.source_id}</span>
              </DetailRow>
              <DetailRow label="User email">
                {q.data.user_email ?? "—"}
              </DetailRow>
              <DetailRow label="Deal">
                {q.data.deal_id ? (
                  <Link
                    to="/deals/$dealId"
                    params={{ dealId: q.data.deal_id }}
                    className="text-brand-ink underline"
                  >
                    {q.data.deal_name ?? q.data.deal_id.slice(0, 8) + "…"}
                  </Link>
                ) : (
                  "—"
                )}
              </DetailRow>
              <DetailRow label="Stage">{q.data.stage ?? "—"}</DetailRow>
              <DetailRow label="Error class">
                {q.data.error_class ?? "—"}
              </DetailRow>
              <DetailRow label="Persisted error class">
                {q.data.persisted_error_class ?? "—"}
              </DetailRow>
              <DetailRow label="Anthropic request id">
                {q.data.anthropic_request_id ?? "—"}
              </DetailRow>
              <DetailRow label="Retry count">
                {q.data.retry_count ?? "—"}
              </DetailRow>
              <DetailRow label="Refund disposition">
                {q.data.refund_disposition ?? "—"}
              </DetailRow>

              <div>
                <p className="mb-1 text-[12.5px] uppercase tracking-wider text-brand-muted">
                  Error message
                </p>
                <pre className="overflow-x-auto whitespace-pre-wrap rounded-lg border border-brand-line bg-background p-3 text-[12.5px] text-brand-ink">
                  {q.data.error_message ?? "(no message)"}
                </pre>
              </div>

              {q.data.raw_properties_json && (
                <div>
                  <p className="mb-1 text-[12.5px] uppercase tracking-wider text-brand-muted">
                    Raw context
                  </p>
                  <pre className="max-h-[280px] overflow-auto whitespace-pre-wrap rounded-lg border border-brand-line bg-background p-3 font-mono text-[11.5px] text-brand-muted">
                    {q.data.raw_properties_json}
                  </pre>
                </div>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function DetailRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-brand-line/50 py-1.5 last:border-b-0">
      <span className="text-[12.5px] text-brand-muted">{label}</span>
      <span className="text-right text-[13.5px] text-brand-ink">{children}</span>
    </div>
  );
}
